import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Plus, X } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { newslettersApi, signupFormsApi, SignupFormRecord, extractLookupId, NewsletterTagRow, NewsletterCategoryRuleRow, NewsletterMailingListRow } from '../services/creatorApi';
import { crmMailingListsApi, crmTagsApi, crmCategoriesApi, CrmMailingList, CrmTag, CrmCategory } from '../services/crmApi';
import { toast } from 'sonner';
function extractFormFields(html: string): string[] {
  const fields: string[] = [];
  const regex = /(?:name|id)=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const name = match[1];
    if (name && !fields.includes(name) && !['submit', 'button'].includes(name.toLowerCase())) {
      fields.push(name);
    }
  }
  return fields;
}
export function NewsletterEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState<SignupFormRecord[]>([]);
  const [mailingLists, setMailingLists] = useState<CrmMailingList[]>([]);
  const [tags, setTags] = useState<CrmTag[]>([]);
  const [categories, setCategories] = useState<CrmCategory[]>([]);
  const [selectedFormHtml, setSelectedFormHtml] = useState('');
  // Form Data
  const [formData, setFormData] = useState({
    name: '',
    newsletterId: '',
    formId: '',
    mailingListIds: [] as string[],
    tagIds: [] as string[],
    categoryRules: [] as NewsletterCategoryRuleRow[]
  });
  // Inline Create State
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  // Fetch Data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [formsResult, listsResult, tagsResult, catsResult] = await Promise.all([signupFormsApi.list(), crmMailingListsApi.list(), crmTagsApi.list(), crmCategoriesApi.list()]);
        const formsData = formsResult;
        const listsData = listsResult;
        const tagsData = tagsResult;
        const catsData = catsResult;
        // Filter forms by team so users only see their own team's forms
        const teamFilteredForms = formsData.filter(f => f.Team === currentWorkspace.name || f.Team === (currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '')));
        setForms(teamFilteredForms);
        setMailingLists(listsData);
        setTags(tagsData);
        setCategories(catsData);
        if (isEditMode && id) {
          const newsletter = await newslettersApi.get(id);
          if (newsletter) {
            const tagIds = (newsletter.Newsletter_Tags || []).map((t: NewsletterTagRow) => t.Tag_ID);
            const mailingListIds = (newsletter.Mailing_Lists || []).map((l: NewsletterMailingListRow) => l.Mailing_List_ID);
            // Keep the full category rules (with IDs)
            const categoryRules = newsletter.Newsletter_Category_Rules || [];
            const selectedFormId = extractLookupId(newsletter.Sign_Up_Form);
            setFormData({
              name: newsletter.Newsletter,
              newsletterId: newsletter.Newsletter_ID || '',
              formId: selectedFormId,
              mailingListIds,
              tagIds,
              categoryRules
            });
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, isEditMode]);
  // Fetch Form HTML when formId changes to extract fields
  useEffect(() => {
    if (!formData.formId) {
      setSelectedFormHtml('');
      return;
    }
    signupFormsApi.get(formData.formId).then(record => {
      if (record) {
        setSelectedFormHtml(record.HTML || '');
      }
    }).catch(err => console.error('Failed to fetch form details', err));
  }, [formData.formId]);
  const formFields = selectedFormHtml ? extractFormFields(selectedFormHtml) : [];
  // Handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Newsletter name is required');
      return;
    }
    setSaving(true);
    try {
      // Build subform rows for Tags — use CRM tag record ID directly as subform ID
      const tagRows: NewsletterTagRow[] = formData.tagIds.map(tagId => {
        const tag = tags.find(t => t.id === tagId);
        if (!tag) return null;
        return {
          ID: tag.id,
          Tag_ID: tag.id,
          Tag_Name: tag.Name
        };
      }).filter(Boolean) as NewsletterTagRow[];
      // Build subform rows for Mailing Lists — use CRM mailing list record ID directly as subform ID
      const mailingListRows: NewsletterMailingListRow[] = formData.mailingListIds.map(listId => {
        const list = mailingLists.find(l => l.id === listId);
        if (!list) return null;
        return {
          ID: list.id,
          Mailing_List_ID: list.id,
          Mailing_List_Name: list.Name
        };
      }).filter(Boolean) as NewsletterMailingListRow[];
      const newsletterId = formData.newsletterId || newslettersApi.generateId();
      const payload = {
        Newsletter: formData.name,
        Newsletter_ID: newsletterId,
        Sign_Up_Form: formData.formId,
        Team: currentWorkspace.name,
        Region: currentSubRegion || '',
        Mailing_Lists: mailingListRows,
        Newsletter_Tags: tagRows,
        Newsletter_Category_Rules: formData.categoryRules
      };
      if (isEditMode && id) {
        await newslettersApi.update(id, payload);
        toast.success('Newsletter updated');
      } else {
        await newslettersApi.create(payload);
        toast.success('Newsletter created');
      }
      navigate('/newsletters');
    } catch (err) {
      console.error('Failed to save newsletter:', err);
      toast.error('Failed to save newsletter');
    } finally {
      setSaving(false);
    }
  };
  // Inline Create Handlers
  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    setCreatingList(true);
    try {
      await crmMailingListsApi.create({
        Name: newListName,
        Team: currentWorkspace.name,
        Region: currentSubRegion || undefined,
        Status: 'open'
      });
      const updatedLists = await crmMailingListsApi.list();
      setMailingLists(updatedLists);
      const newList = updatedLists.find(l => l.Name === newListName);
      if (newList) {
        setFormData(prev => ({
          ...prev,
          mailingListIds: [...prev.mailingListIds, newList.id]
        }));
      }
      setShowCreateList(false);
      setNewListName('');
      toast.success('Mailing list created');
    } catch (err) {
      toast.error('Failed to create mailing list');
    } finally {
      setCreatingList(false);
    }
  };
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setCreatingTag(true);
    try {
      await crmTagsApi.create({
        Name: newTagName,
        Team: currentWorkspace.name
      });
      const updatedTags = await crmTagsApi.list();
      setTags(updatedTags);
      const newTag = updatedTags.find(t => t.Name === newTagName);
      if (newTag) {
        setFormData(prev => ({
          ...prev,
          tagIds: [...prev.tagIds, newTag.id]
        }));
      }
      setShowCreateTag(false);
      setNewTagName('');
      toast.success('Tag created');
    } catch (err) {
      toast.error('Failed to create tag');
    } finally {
      setCreatingTag(false);
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5396" data-mp="ssx0mvbrroz1">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-5397" data-mp="ntk9vzhs1xr6" />
      </div>;
  }
  return <div className="space-y-6 max-w-4xl pb-20" data-id="element-5398" data-mp="12igji0zhfitw">
      <div className="flex items-center gap-4" data-id="element-5399" data-mp="12j9igfs7941h">
        <Link to="/newsletters" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-5400" data-mp="xqoo9d5dsoxf">
          <ArrowLeft className="h-5 w-5" data-id="element-5401" data-mp="a0cl0fl2zgd6" />
        </Link>
        <div data-id="element-5402" data-mp="52kkwyaa8nxw">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5403" data-mp="532okfz9y4fu">
            {isEditMode ? 'Edit Newsletter' : 'Create Newsletter'}
          </h1>
          <p className="text-sm text-gray-500" data-id="element-5404" data-mp="2golabvhy946m">
            Configure audience lists and CRM integration.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8" data-id="element-5405" data-mp="26s05ba5xjt2s">
        {/* Basic Info */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6" data-id="element-5406" data-mp="2bqt8o9ri63p8">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-5407" data-mp="2zge43s4uetfe">
            Basic Information
          </h2>
          <div className="grid grid-cols-1 gap-6" data-id="element-5408" data-mp="34fgvi9rwh14w">
            <div data-id="element-5409" data-mp="34eyt0ven9qoj">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5410" data-mp="34deak45lty71">
                Newsletter Name
              </label>
              <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} placeholder="e.g., Weekly Updates" data-id="element-5411" data-mp="2uh17ifo667zm" />
            </div>

            {isEditMode && formData.newsletterId && <div data-id="element-5412" data-mp="ocyj8mas22l7">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5413" data-mp="sesegf27eedz">
                  Newsletter ID
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed font-mono" value={formData.newsletterId} disabled data-id="element-5414" data-mp="eerauruk0sut" />
                <p className="mt-1 text-xs text-gray-400" data-id="element-5415" data-mp="9f625ppl164w">
                  Unique identifier used to link form submissions to this
                  newsletter.
                </p>
              </div>}

            <div data-id="element-5416" data-mp="23sxl5ftsx2d0">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5417" data-mp="23tffg6wwl6ia">
                Sign Up Form
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.formId} onChange={e => setFormData({
              ...formData,
              formId: e.target.value
            })} data-id="element-5418" data-mp="3o9nbbn8mrhiy">
                <option value="" data-id="element-5419" data-mp="bwfkteisnu8u">Select a form...</option>
                {forms.map(f => <option key={f.ID} value={f.ID} data-id="element-5420" data-mp="6xm5dgjy5t61">
                    {f.Form_Name}
                  </option>)}
              </select>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2" data-id="element-5421" data-mp="fxhj9t4w029r">
              <h3 className="text-sm font-medium text-gray-900" data-id="element-5422" data-mp="2mm4c1telidrj">Scope</h3>
              <div className="text-sm text-gray-600 grid grid-cols-2 gap-4" data-id="element-5423" data-mp="2hnko5ngfyrwj">
                <div data-id="element-5424" data-mp="2hpnnwz7ebzeg">
                  <strong data-id="element-5425" data-mp="lkel6gk25hu4">Team:</strong> {currentWorkspace.name}
                </div>
                <div data-id="element-5426" data-mp="lmrbbgzyyyi9">
                  <strong data-id="element-5427" data-mp="qj9b02gg06nq">Region:</strong> {currentSubRegion || 'All Regions'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CRM Integration */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6" data-id="element-5428" data-mp="zjxrr4x80l4b">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-5429" data-mp="14jac9j0mdg3q">CRM Integration</h2>

          {/* Mailing Lists (Multi-select) */}
          <div data-id="element-5430" data-mp="19hllpsn05olf">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5431" data-mp="19h2fvaammqnr">
              Assign to Mailing Lists
            </label>
            <div className="space-y-2" data-id="element-5432" data-mp="1eb6lnq85osn9">
              <div className="flex flex-wrap gap-2 mb-2" data-id="element-5433" data-mp="1j8gab3nq10a1">
                {formData.mailingListIds.map(listId => {
                const list = mailingLists.find(l => l.id === listId);
                return list ? <span key={listId} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-50 text-blue-700 border border-blue-200" data-id="element-5434" data-mp="1s9efvqdt5fuq">
                      {list.Name}
                      <button type="button" onClick={() => setFormData(prev => ({
                    ...prev,
                    mailingListIds: prev.mailingListIds.filter(id => id !== listId)
                  }))} className="hover:text-blue-900" data-id="element-5435" data-mp="33ppi0qoag1th">
                        <X className="h-3 w-3" data-id="element-5436" data-mp="3hq8kzjdwchos" />
                      </button>
                    </span> : null;
              })}
              </div>
              <div className="flex gap-2" data-id="element-5437" data-mp="j232joamnli6">
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value="" onChange={e => {
                if (e.target.value && !formData.mailingListIds.includes(e.target.value)) {
                  setFormData(prev => ({
                    ...prev,
                    mailingListIds: [...prev.mailingListIds, e.target.value]
                  }));
                }
              }} data-id="element-5438" data-mp="e8qebf9v1gpy">
                  <option value="" data-id="element-5439" data-mp="7sq8d359t95r">Add a mailing list...</option>
                  {mailingLists.filter(l => !formData.mailingListIds.includes(l.id)).map(l => <option key={l.id} value={l.id} data-id="element-5440" data-mp="10fu8outf4zb7">
                        {l.Name}
                      </option>)}
                </select>
                <button type="button" onClick={() => setShowCreateList(true)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" title="Create new mailing list" data-id="element-5441" data-mp="1elw9lwtl610a">
                  <Plus className="h-4 w-4" data-id="element-5442" data-mp="1jdmkc3wfqqs8" />
                </button>
              </div>
              {showCreateList && <div className="mt-2 flex gap-2 items-center bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-5443" data-mp="rlon287gynak">
                  <input type="text" placeholder="New mailing list name" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newListName} onChange={e => setNewListName(e.target.value)} data-id="element-5444" data-mp="15l6c8ooog2kg" />
                  <button type="button" onClick={handleCreateList} disabled={creatingList || !newListName.trim()} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-id="element-5445" data-mp="2c3w7t59nj8qr">
                    {creatingList ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5446" data-mp="20eqqzg5gf3t7" /> : 'Create'}
                  </button>
                  <button type="button" onClick={() => setShowCreateList(false)} className="p-2 text-gray-400 hover:text-gray-600" data-id="element-5447" data-mp="25az5qsfqouzu">
                    <X className="h-4 w-4" data-id="element-5448" data-mp="1ho0ovtnpjl1e" />
                  </button>
                </div>}
            </div>
          </div>

          {/* Tags (Multi-select) */}
          <div data-id="element-5449" data-mp="1mkhcmmqzvxdy">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5450" data-mp="1f3b64e4qpmb1">
              Assign Tags
            </label>
            <div className="space-y-2" data-id="element-5451" data-mp="1a3ygjlmhi14r">
              <div className="flex flex-wrap gap-2 mb-2" data-id="element-5452" data-mp="1a4zrlq3i2nmh">
                {formData.tagIds.map(tagId => {
                const tag = tags.find(t => t.id === tagId);
                return tag ? <span key={tagId} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-50 text-blue-700 border border-blue-200" data-id="element-5453" data-mp="1084u9aiyqt9s">
                      {tag.Name}
                      <button type="button" onClick={() => setFormData(prev => ({
                    ...prev,
                    tagIds: prev.tagIds.filter(id => id !== tagId)
                  }))} className="hover:text-blue-900" data-id="element-5454" data-mp="mfoymuse5xiw">
                        <X className="h-3 w-3" data-id="element-5455" data-mp="1wcviqjse69zl" />
                      </button>
                    </span> : null;
              })}
              </div>
              <div className="flex gap-2" data-id="element-5456" data-mp="1mdw9p03pscc2">
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value="" onChange={e => {
                if (e.target.value && !formData.tagIds.includes(e.target.value)) {
                  setFormData(prev => ({
                    ...prev,
                    tagIds: [...prev.tagIds, e.target.value]
                  }));
                }
              }} data-id="element-5457" data-mp="1mcuzsyyfpvvx">
                  <option value="" data-id="element-5458" data-mp="un048kjzh9yt">Add a tag...</option>
                  {tags.filter(t => !formData.tagIds.includes(t.id)).map(t => <option key={t.id} value={t.id} data-id="element-5459" data-mp="g01gazkc4xou">
                        {t.Name}
                      </option>)}
                </select>
                <button type="button" onClick={() => setShowCreateTag(true)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" title="Create new tag" data-id="element-5460" data-mp="p0pnbabb9xs8">
                  <Plus className="h-4 w-4" data-id="element-5461" data-mp="tx6l6w8bbqii" />
                </button>
              </div>
              {showCreateTag && <div className="mt-2 flex gap-2 items-center bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-5462" data-mp="13p2meg5p3wqy">
                  <input type="text" placeholder="New tag name" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newTagName} onChange={e => setNewTagName(e.target.value)} data-id="element-5463" data-mp="1hobyp4qs66pn" />
                  <button type="button" onClick={handleCreateTag} disabled={creatingTag || !newTagName.trim()} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-id="element-5464" data-mp="1kbiar9j7vitj">
                    {creatingTag ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5465" data-mp="1g7k9otabzxxu" /> : 'Create'}
                  </button>
                  <button type="button" onClick={() => setShowCreateTag(false)} className="p-2 text-gray-400 hover:text-gray-600" data-id="element-5466" data-mp="1bf0vbu3tkyrz">
                    <X className="h-4 w-4" data-id="element-5467" data-mp="iqlq1zahputu" />
                  </button>
                </div>}
            </div>
          </div>

          {/* Categories (Conditional Rules) */}
          <div className="pt-4 border-t border-gray-200" data-id="element-5468" data-mp="2z8l14cc3peza">
            <div className="flex items-center justify-between mb-2" data-id="element-5469" data-mp="2kinei0ttkxc7">
              <label className="block text-sm font-medium text-gray-700" data-id="element-5470" data-mp="2pg5mnp00j8j5">
                Conditional Category Rules
              </label>
              <button type="button" onClick={() => {
              setFormData(prev => ({
                ...prev,
                categoryRules: [...prev.categoryRules, {
                  Form_Field: formFields[0] || '',
                  Condition: 'equals',
                  Value: '',
                  Category_ID: '',
                  Category_Name: ''
                }]
              }));
            }} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-5471" data-mp="3d6swiftho88c">
                + Add Rule
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4" data-id="element-5472" data-mp="3uhy7vv77ch6w">
              Assign CRM categories to subscribers based on their form field
              values.
            </p>

            {formData.categoryRules.length > 0 ? <div className="space-y-3" data-id="element-5473" data-mp="3goqepz3h3dn3">
                {formData.categoryRules.map((rule, index) => <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-md space-y-3 relative" data-id="element-5474" data-mp="3boujopcr6wjo" data-mpi={index}>
                    <button type="button" onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  categoryRules: prev.categoryRules.filter((_, i) => i !== index)
                }));
              }} className="absolute top-2 right-2 text-gray-400 hover:text-red-500" data-id="element-5475" data-mp="31r6cv11vhyl5">
                      <X className="h-4 w-4" data-id="element-5476" data-mp="1h95mt4wpyvtd" />
                    </button>
                    <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-5477" data-mp="1hayrienrv5yq">
                      Rule {index + 1}
                    </div>

                    <div className="grid grid-cols-3 gap-2" data-id="element-5478" data-mp="pihoe0mfk4je">
                      <div className="col-span-1" data-id="element-5479" data-mp="tjbacogbb99n">
                        <label className="block text-[10px] text-gray-500 mb-1" data-id="element-5480" data-mp="tk2u920jrik3">
                          Field
                        </label>
                        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.Form_Field} onChange={e => {
                    const newRules = [...formData.categoryRules];
                    newRules[index].Form_Field = e.target.value;
                    setFormData({
                      ...formData,
                      categoryRules: newRules
                    });
                  }} data-id="element-5481" data-mp="ykqso8r3v1oc">
                          <option value="" data-id="element-5482" data-mp="3ru4rwpvs297a">Select field...</option>
                          {formFields.map(f => <option key={f} value={f} data-id="element-5483" data-mp="alhvqci37wbu">
                              {f}
                            </option>)}
                        </select>
                      </div>
                      <div className="col-span-1" data-id="element-5484" data-mp="jel7mgd3414d">
                        <label className="block text-[10px] text-gray-500 mb-1" data-id="element-5485" data-mp="fdj1rgonwqtf">
                          Condition
                        </label>
                        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.Condition} onChange={e => {
                    const newRules = [...formData.categoryRules];
                    newRules[index].Condition = e.target.value;
                    setFormData({
                      ...formData,
                      categoryRules: newRules
                    });
                  }} data-id="element-5486" data-mp="tbg28o68lhdd">
                          <option value="equals" data-id="element-5487" data-mp="u0zy0wo8z7ro">Equals</option>
                          <option value="not_equals" data-id="element-5488" data-mp="u2b08s21o7go">Not Equals</option>
                          <option value="contains" data-id="element-5489" data-mp="fd52z9mrorbf">Contains</option>
                        </select>
                      </div>
                      <div className="col-span-1" data-id="element-5490" data-mp="k8c1m316j5th">
                        <label className="block text-[10px] text-gray-500 mb-1" data-id="element-5491" data-mp="6bykww4gz8g2">
                          Value
                        </label>
                        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.Value} onChange={e => {
                    const newRules = [...formData.categoryRules];
                    newRules[index].Value = e.target.value;
                    setFormData({
                      ...formData,
                      categoryRules: newRules
                    });
                  }} placeholder="e.g. Male" data-id="element-5492" data-mp="aebobdvquu9o" />
                      </div>
                    </div>

                    <div data-id="element-5493" data-mp="2hxpj62fcvbhi">
                      <label className="block text-[10px] text-gray-500 mb-1" data-id="element-5494" data-mp="2i0li9u2xw67j">
                        Assign Category
                      </label>
                      <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.Category_ID} onChange={e => {
                  const newRules = [...formData.categoryRules];
                  const catId = e.target.value;
                  const cat = categories.find(c => c.id === catId);
                  newRules[index].Category_ID = catId;
                  newRules[index].Category_Name = cat ? cat.Name : '';
                  setFormData({
                    ...formData,
                    categoryRules: newRules
                  });
                }} data-id="element-5495" data-mp="16i0wlc60keh8">
                        <option value="" data-id="element-5496" data-mp="iqsefqdn3xg5">Select category...</option>
                        {categories.map(cat => <option key={cat.id} value={cat.id} data-id="element-5497" data-mp="2flooz3hteuqo">
                            {cat.Name}
                          </option>)}
                      </select>
                    </div>
                  </div>)}
              </div> : <div className="text-center py-6 bg-gray-50 border border-gray-200 border-dashed rounded-md" data-id="element-5498" data-mp="m75943zq8k6j">
                <p className="text-sm text-gray-500" data-id="element-5499" data-mp="102h2o825c4lv">
                  No category rules defined.
                </p>
                <button type="button" onClick={() => {
              setFormData(prev => ({
                ...prev,
                categoryRules: [{
                  Form_Field: formFields[0] || '',
                  Condition: 'equals',
                  Value: '',
                  Category_ID: '',
                  Category_Name: ''
                }]
              }));
            }} className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-5500" data-mp="7bpadlo7c03t">
                  Add your first rule
                </button>
              </div>}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-5501" data-mp="2wn5lz4er4tlh">
          <Link to="/newsletters" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5502" data-mp="2wp8ga0mxt8w0">
            Cancel
          </Link>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-5503" data-mp="3td6hdm9mm8br">
            {saving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5504" data-mp="3kjjoq50ptl6s" />}
            Save Newsletter
          </button>
        </div>
      </form>
    </div>;
}